import { Inject, Injectable } from '@angular/core';
import { VendorSalesRep } from '../models/vendorSalesRep.model';

import { ENV, IEnvironment } from 'env/config';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { Service } from '../entity';
import { DataService } from '../http/dataService';

import { Dropdown, Result, TDropdown } from '../interfaces/helpers';
import { RepRosterResult } from '../interfaces/internal';
import { IRepRosterVendorGroup } from '../models/repRoster.model';
import { VendorGroup } from '../models/vendorGroup.model';
import { Repository } from './repository';

import { RepRosterService } from './repRoster';

@Service({
  model: VendorGroup,
})
@Injectable({ providedIn: 'root' })
export class VendorGroupService extends Repository<VendorGroup> {
  constructor(@Inject(ENV) env: IEnvironment, dataService: DataService) {
    super({
      client: dataService,
      url: env.api.backend.reproster.endpoint as string,
    });
  }

  static getParams(queryParams?, conditions?, isCount = false) {
    const filter = queryParams ?? {};

    if (conditions) {
      if (conditions.condition.indexOf('vendorGroupId') !== -1) {
        filter.id = conditions.value;
      } else if (conditions.condition.indexOf('vendorAccountName') !== -1) {
        filter.vendorAccountName = conditions.value;
      }
    }

    return { q: RepRosterService.getVendorGroupQuery(filter, isCount) };
  }

  getCount(queryParams?: any, conditions?): Observable<RepRosterResult<any>> {
    const vendorGroupParamsCount = VendorGroupService.getParams(
      queryParams,
      conditions,
      true
    );

    const url = `${this.url}${this.api}`;
    return this.dataService.get<RepRosterResult<any>>(
      url,
      vendorGroupParamsCount
    );
  }

  getAll(queryParams?: any, conditions?): Observable<Result<VendorGroup>> {
    const vendorGroupParams = VendorGroupService.getParams(
      queryParams,
      conditions
    );

    const vendorGroupRequest = super.getAll(vendorGroupParams);

    if (!queryParams?.limit) {
      return vendorGroupRequest;
    } else {
      const countVendorGroupRequest = this.getCount(queryParams, conditions);
      return forkJoin([countVendorGroupRequest, vendorGroupRequest]).pipe(
        map((forkResult) => {
          const countResult = forkResult[0];
          const results = forkResult[1];

          return {
            items: results.items,
            links: results.links,
            meta: {
              itemCount: results.items.length,
              totalItems: countResult.totalSize,
              itemsPerPage: queryParams.limit,
              totalPages: Math.ceil(
                countResult.totalSize / results.items.length
              ),
              currentPage: 0,
            },
          };
        })
      );
    }
  }

  getOne(queryParams: any): Observable<VendorGroup> {
    const vendorGroupParams = {
      q: RepRosterService.getVendorGroupQuery(queryParams),
    };

    return super
      .getOne<RepRosterResult<IRepRosterVendorGroup>>(vendorGroupParams)
      .pipe(
        map((data: RepRosterResult<IRepRosterVendorGroup>) => {
          const { records } = data;

          return records.map((vg) => VendorGroup.createFromDTO(vg))[0];
        })
      );
  }

  fetchDropdownData(queryParams?): Observable<Dropdown[]> {
    const newQueryParams = {
      q: RepRosterService.getVendorDropdownQuery(queryParams),
    };

    return super.getAll(newQueryParams).pipe(
      map((data) => {
        const { items } = data;

        return items
          .map((vg) => ({
            value: vg.id.toString(),
            text: vg.vendorName,
            checked: true,
          }))
          .sort((a, b) => (a.text > b.text ? 1 : -1));
      })
    );
  }

  getVendorMemberByVendorGroupId(vendorGroupId: string) {
    return `SELECT Federation_ID__c,Name FROM Vendor_Group_Member__c WHERE Vendor_Group__c = '${vendorGroupId}' `;
  }

  getVendorMemberByCpms(cpmsid: string) {
    return `SELECT CPMS_ID__c,Name FROM Vendor_Group_Member__c WHERE Name = '${cpmsid}' `;
  }

  fetchVendorMembersByVendorGroup(vendorId: string): Observable<Dropdown[]> {
    if (!vendorId) {
      const emptyDropdown: Dropdown[] = [];
      return of(emptyDropdown);
    }

    const queryString = { q: this.getVendorMemberByVendorGroupId(vendorId) };
    queryString.q = queryString.q.split(' ').join('+');

    return this.queryDataVendorMember(queryString).pipe(
      map(
        ({ items }) =>
          items.map(
            (item) =>
              new TDropdown({
                value: item.federationId,
                text: item.vendorName,
                checked: false,
              })
          ),
        catchError((error) => [])
      )
    );
  }

  getMoreData(url: string) {
    const path = url.split('/');
    this.api = path[path.length - 1];

    return super.getAll().pipe(
      map((data: Result<VendorGroup>) => {
        this.api = '';
        return data;
      })
    );
  }

  createFromDTO(item: IRepRosterVendorGroup): VendorGroup {
    return VendorGroup.createFromDTO(item);
  }

  private queryDataVendorMember(queryParams): Observable<Result<VendorGroup>> {
    return super.getAll<RepRosterResult<IRepRosterVendorGroup>>(queryParams);
  }
}
